/**
 * Small formatting helpers shared across views.
 */

/** "2 hours ago", "3 days ago", "just now". */
export function ageString(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const ms = Date.now() - t;
  if (ms < 0) {
    return "just now";
  }
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 8) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

/* FEAT-087 / S-363 — `shortDate` and `shortDateTime` lived here and
 * formatted against the browser's own locale, which now conflicts with the
 * per-user date-format setting. Both are gone rather than deprecated: they
 * are exactly the helpers someone would reach for by habit, and reaching
 * for them would silently bypass the user's choice. Use `formatDate` /
 * `formatDateTime` from `@/lib/date-format` with the setting from
 * `useDateFormat()`. */

export function formatContentType(t: string | null | undefined): string {
  if (!t) return "Piece";
  return t
    .split("_")
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

const CONTENT_TYPE_TONES: Record<string, "blue" | "green" | "yellow" | "neutral"> = {
  blog_post: "blue",
  linkedin_article: "blue",
  substack: "blue",
  newsletter: "blue",
  technical: "neutral",
  internal_memo: "neutral",
  personal: "neutral",
  news_analysis: "yellow",
  short_story: "green",
  serial_episode: "green",
  novella_chapter: "green",
  flash_fiction: "green",
};

export const CONTENT_TYPE_DESCRIPTIONS: Record<string, string> = {
  blog_post: "SEO-optimized web article, typically evergreen. Published to a company blog or website.",
  linkedin_article: "Long-form LinkedIn native article. Professional audience, thought leadership framing.",
  substack: "Newsletter-style article. More personal and editorial than a blog post.",
  technical: "Deep-dive technical content. Assumes domain knowledge; numbers and precision first.",
  internal_memo: "Internal document for a team or stakeholder. TL;DR up front, action items at the end.",
  personal: "Personal essay or opinion piece. First-person voice, anecdote-driven.",
  news_analysis: "Time-sensitive piece responding to recent news. Adds context, POV, and analysis.",
  newsletter: "Periodic curated issue. Summarizes and connects content across sources.",
  short_story: "Complete standalone fictional narrative.",
  serial_episode: "One episode of an ongoing fictional series.",
  novella_chapter: "One chapter of a longer work-in-progress novella.",
  flash_fiction: "Very short fiction, typically under 1,000 words.",
};

export function contentTypeTone(
  t: string | null | undefined
): "blue" | "green" | "yellow" | "neutral" {
  return CONTENT_TYPE_TONES[t ?? ""] ?? "neutral";
}
